/**
 * 16차 ID51 — 고도몰 주문상태 노출이름 단일 맵 (PHASE 2-B).
 *
 * BE 정본: order-service `application/v2/display/OrderStatusLabel`(enum, `code()`=안정 SSoT 키,
 * `displayName()`=한국어 24종) + `StatusLabelCatalog.resolve(DisplayStatus, ClaimType, CancelOrigin)`.
 * **BE 가 단일 도출자**이며, 응답(`OrderSummaryResponse`·`OrderDetailResponse`)에 `displayLabelCode`
 * (= `OrderStatusLabel.code()`, @JsonInclude ALWAYS)를 `displayStatus` 옆에 additive 노출한다.
 * FE 는 도출 금지 — 이 맵으로 코드를 한국어 노출이름으로 1:1 변환해 그대로 표시만 한다.
 *
 * 총 31 코드 = 배송 25(DisplayLabelCode, 고도몰 24명칭 — '반송중'이 RETURN_SHIPPING·EXCHANGE_SHIPPING
 * 두 코드에 공통) + 픽업 6(PickupLabelCode, 별도 축 — 아래 PICKUP_* 참조). `labelNameForCode`는
 * 양 축(배송 25 + 픽업 6)을 모두 **primary**로 해석한다(픽업도 폴백 의존 아님).
 * code 값은 BE `OrderStatusLabel` enum 과 정확히 일치(QA 패리티 기준, Lead 락 확정).
 * 근거: BE-ORDER 카탈로그 SSoT(2026-06-05) / everline-qa-51 §2-1·§6-7,8.
 *
 * ⚠ BO 는 한국어 전용(다국어 무관). displayName 을 그대로 사용한다.
 */

/** BE `OrderStatusLabel.code()`(displayLabelCode 값) — 배송 25 코드. 픽업 6은 PickupLabelCode 참조. */
export type DisplayLabelCode =
  | 'AWAITING_DEPOSIT' // 1 입금대기
  | 'PAID' // 2 결제완료
  | 'PREPARING' // 3 상품준비중
  | 'SHIPPING' // 4 배송중
  | 'DELIVERED' // 5 배송완료
  | 'PURCHASE_CONFIRMED' // 6 구매확정
  | 'CANCEL_AUTO' // 7 자동취소
  | 'CANCEL_SOLDOUT' // 8 품절취소
  | 'CANCEL_ADMIN' // 9 관리자취소
  | 'CANCEL_CUSTOMER' // 10 고객요청취소
  | 'PAYMENT_STOPPED' // 11 고객결제중단 (도출 보류 — code/이름 확정)
  | 'PAYMENT_FAILED' // 12 결제실패
  | 'PG_CHECK' // 13 PG확인요망 (도출 보류)
  // QA-530(order #104) 승인 전 = XX신청, 승인 후(회수 대기) = XX접수.
  | 'RETURN_APPLIED' // 반품신청
  | 'RETURN_REQUESTED' // 14 반품접수
  | 'RETURN_SHIPPING' // 15 반송중(반품)
  | 'RETURN_HOLD' // 16 반품보류
  | 'RETURN_COLLECTED' // 17 반품회수완료
  | 'EXCHANGE_APPLIED' // 교환신청
  | 'EXCHANGE_REQUESTED' // 18 교환접수
  | 'EXCHANGE_SHIPPING' // 19 반송중(교환)
  | 'EXCHANGE_RESHIPPING' // 20 재배송중 (Q2 도출 채택)
  | 'EXCHANGE_HOLD' // 21 교환보류
  | 'EXCHANGE_DONE' // 22 교환완료
  | 'REFUND_APPLIED' // 환불신청
  | 'REFUND_REQUESTED' // 23 환불접수
  | 'REFUND_HOLD' // 24 환불보류 (도출 보류)
  | 'REFUND_DONE' // 25 환불완료
  // QA-530 재이슈 — [XX접수] 반려는 "XX보류"가 아니라 "XX반려"(order #102).
  | 'RETURN_REJECTED' // 반품반려
  | 'EXCHANGE_REJECTED' // 교환반려
  | 'REFUND_REJECTED'; // 환불반려

/**
 * displayLabelCode → 한국어 노출이름. 25 코드 전부. BE `displayName()` 과 1:1.
 * '반송중'은 코드 15(RETURN_SHIPPING)·19(EXCHANGE_SHIPPING)가 같은 표시명을 갖는다(명칭 24종).
 */
export const DISPLAY_LABEL_CODE_NAMES: Readonly<Record<DisplayLabelCode, string>> = {
  AWAITING_DEPOSIT: '입금대기',
  PAID: '결제완료',
  PREPARING: '상품준비중',
  SHIPPING: '배송중',
  DELIVERED: '배송완료',
  PURCHASE_CONFIRMED: '구매확정',
  CANCEL_AUTO: '자동취소',
  CANCEL_SOLDOUT: '품절취소',
  CANCEL_ADMIN: '관리자취소',
  CANCEL_CUSTOMER: '고객요청취소',
  PAYMENT_STOPPED: '고객결제중단',
  PAYMENT_FAILED: '결제실패',
  PG_CHECK: 'PG확인요망',
  RETURN_APPLIED: '반품신청',
  RETURN_REQUESTED: '반품접수',
  RETURN_SHIPPING: '반송중',
  RETURN_HOLD: '반품보류',
  RETURN_COLLECTED: '반품회수완료',
  EXCHANGE_APPLIED: '교환신청',
  EXCHANGE_REQUESTED: '교환접수',
  EXCHANGE_SHIPPING: '반송중',
  EXCHANGE_RESHIPPING: '재배송중',
  EXCHANGE_HOLD: '교환보류',
  EXCHANGE_DONE: '교환완료',
  REFUND_APPLIED: '환불신청',
  REFUND_REQUESTED: '환불접수',
  REFUND_HOLD: '환불보류',
  REFUND_DONE: '환불완료',
  RETURN_REJECTED: '반품반려',
  EXCHANGE_REJECTED: '교환반려',
  REFUND_REJECTED: '환불반려',
};

/**
 * 픽업 주문 displayLabelCode — **별도 축**(Lead 확정, BE policy-core §1.6).
 * 픽업 6상태는 고도몰 배송 24 노출이름으로 강제하지 않는다. 픽업 주문의 `displayLabelCode`는
 * 아래 PICKUP_* 코드로 그대로 노출되며, BO 는 이 픽업 축 라벨을 별도 키군으로 유지한다.
 * (orders.json `status.PICKUP_*` 와 동일 한글.)
 */
export type PickupLabelCode =
  | 'PICKUP_WAITING_PREP' // 준비대기
  | 'PICKUP_READY' // 수령가능
  | 'PICKUP_IN_PROGRESS' // 수령진행중
  | 'PICKUP_COMPLETED' // 수령완료
  | 'PICKUP_NO_SHOW' // 미수령
  | 'PICKUP_CANCELED'; // 픽업취소

export const PICKUP_LABEL_CODE_NAMES: Readonly<Record<PickupLabelCode, string>> = {
  PICKUP_WAITING_PREP: '준비대기',
  PICKUP_READY: '수령가능',
  PICKUP_IN_PROGRESS: '수령진행중',
  PICKUP_COMPLETED: '수령완료',
  PICKUP_NO_SHOW: '미수령',
  PICKUP_CANCELED: '픽업취소',
};

/**
 * 배송 25 + 픽업 6 통합 조회 테이블. BE 가 displayLabelCode 로 두 축 중 하나를 내리므로
 * 단일 변환 함수가 양쪽을 모두 해석할 수 있어야 한다.
 */
const ALL_LABEL_CODE_NAMES: Readonly<Record<string, string>> = {
  ...DISPLAY_LABEL_CODE_NAMES,
  ...PICKUP_LABEL_CODE_NAMES,
};

/** 미지/누락 코드 폴백 (raw 코드 노출 방지). */
const FALLBACK = '-';

/**
 * BE `displayLabelCode` 를 한국어 노출이름으로 안전 변환한다(배송 25 + 픽업 6 양 축).
 * null/undefined/미지 코드면 fallback 을 반환(raw key 노출 방지). FE 는 도출하지 않는다.
 */
export const labelNameForCode = (
  code: string | null | undefined,
  fallback: string = FALLBACK,
): string => {
  if (!code) return fallback;
  return ALL_LABEL_CODE_NAMES[code] ?? fallback;
};
